import React from 'react';

import { reverse } from 'named-urls';

import { MainHeader } from 'components/MainHeader/MainHeader';

import { Button } from 'components/Button';

import routes from 'core/routes';

import CustomTextInputField from 'components/SharedFormComponents/CustomTextInputField';
import CustomModal from 'components/CustomModal';

import { EntityModules } from 'core/constants';

import API from 'utils/api/api';
import { useStore } from 'utils/hooks/store';

import { modifyInputValue } from 'containers/FieldManager/components/CreateEditFieldModal/types';

import { DetailsRowContainer, TitleText } from './styles';

const AddPipelineModal: React.FunctionComponent<{
  onClose: (created: boolean) => void;
}> = ({ onClose }): React.ReactElement => {
  const [name, setName] = React.useState('');
  const [nameError, setNameError] = React.useState('');

  const { notification } = useStore();

  const handleCreate = async (): Promise<void> => {
    if (name.length > 255) {
      setNameError('Name must be 255 characters or less');
      return;
    }

    try {
      const key = modifyInputValue(name);

      await API.entities.createPipeline(EntityModules.Events, name, key)();
      notification.enqueueSuccessSnackbar('Pipeline created successfully');
      onClose(true);
    } catch (error) {
      notification.enqueueErrorSnackbar('Failed to create pipeline');
    }
  };

  const handleNameChange = (newName: string): void => {
    setName(newName);
    if (newName.length > 255) {
      setNameError('Name must be 255 characters or less');
    } else {
      setNameError('');
    }
  };

  return (
    <CustomModal
      openModal
      onClose={() => onClose(false)}
      modalTitle="New pipeline"
      cancelButton={
        <Button
          id="cancel-button"
          onClick={() => onClose(false)}
          variant="outlined"
          size="large"
          data-testid="cancelButton"
        >
          Cancel
        </Button>
      }
      submitButton={
        <Button
          id="create-action-button"
          onClick={handleCreate}
          disabled={name === ''}
          variant="contained"
          size="large"
          data-testid="addButton"
        >
          Create
        </Button>
      }
    >
      <DetailsRowContainer>
        <TitleText>Name</TitleText>
        <CustomTextInputField
          value={name}
          onChange={(e) => handleNameChange(e as unknown as string)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleCreate();
            }
          }}
          description="Enter name"
          requiredNotFilled={!!nameError}
          errorMessage={nameError}
        />
      </DetailsRowContainer>
    </CustomModal>
  );
};

const PipelinesHeader = ({
  refreshRef,
}: {
  refreshRef: React.MutableRefObject<Function | null>;
}): React.ReactElement => {
  const [open, setOpen] = React.useState(false);

  const openModal = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);

    if (refreshRef.current) {
      refreshRef.current();
    }
  };

  return (
    <>
      <MainHeader
        title="Pipelines"
        breadcrumbs={[
          {
            label: 'Objects',
            link: '',
          },
          {
            label: 'Events',
            link: reverse(routes.dashboard.settings.objects.details.formBuilder.toString(), {
              moduleName: EntityModules.Events,
            }),
          },
          {
            label: 'Pipelines',
            link: reverse(routes.dashboard.settings.objects.details.pipelines.toString(), {
              moduleName: EntityModules.Events,
            }),
          },
        ]}
        action={
          <Button data-testid="new-form-btn" variant="contained" onClick={openModal} size="large">
            New
          </Button>
        }
      />
      {open && <AddPipelineModal onClose={handleClose} />}
    </>
  );
};

export default PipelinesHeader;
